import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Mail, ChevronDown, Menu, X, User } from 'lucide-react';
import { Button } from './ui/button';
import { CONTACT_INFO } from '../config/constants';
import { useBuyer } from '../contexts/BuyerAuthContext';

const NewNavbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileExpandedDropdown, setMobileExpandedDropdown] = useState(null);
  const location = useLocation();
  const { buyer, isAuthenticated } = useBuyer();
  const desktopNavRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  // Close menus on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  // Close desktop dropdown when clicking/touching outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (desktopNavRef.current && !desktopNavRef.current.contains(e.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { name: 'Home', path: '/' },
    {
      name: 'Commercial',
      path: '/commercial',
      dropdown: [
        { name: 'Business for Sale', path: '/listings' },
        { name: 'Healthcare Listings', path: '/healthcare-opportunities' },
      ]
    },
    {
      name: 'Residential',
      path: '#',
      isExternal: true,
      dropdown: [
        { name: 'Buy House', path: 'https://teamshailesh.searchtorontoproperties.com/', isExternal: true },
        { name: 'Sell House', path: 'https://teamshailesh.searchtorontoproperties.com/', isExternal: true },
        { name: 'Invest', path: 'https://teamshailesh.searchtorontoproperties.com/', isExternal: true },
      ]
    },
    {
      name: 'Services',
      path: '/services',
      dropdown: [
        { name: 'Buy a Business', path: '/services/buy' },
        { name: 'Grow Your Business', path: '/services/grow' },
        { name: 'Sell a Business', path: '/services/sell' },
        { name: '★ Free Business Valuation', path: '/what-is-my-business-worth' },
      ]
    },
    {
      name: 'About Us',
      path: '/about',
      dropdown: [
        { name: 'Our Team', path: '/about/team' },
        { name: 'Testimonials', path: '/about/testimonials' },
        { name: 'Join Us', path: '/about/join' },
      ]
    },
    { name: 'Contact', path: '/contact' },
    { name: 'Meet our Founder', path: '/founder' },
    { name: 'Blog', path: '/blog' },
    {
      name: 'Locations',
      path: '#',
      dropdown: [
        { name: 'Toronto', path: '/location/toronto' },
        { name: 'Brampton', path: '/location/brampton' },
        { name: 'Mississauga', path: '/location/mississauga' },
        { name: 'Vaughan', path: '/location/vaughan' },
        { name: 'Oakville', path: '/location/oakville' },
        { name: 'Hamilton', path: '/location/hamilton' },
      ]
    },
  ];

  return (
    <>
      {/* Top Contact Bar */}
      <div className="bg-[#1e3a8a] text-white py-2 hidden md:block">
        <div className="container mx-auto px-4">
          <div className="flex justify-end items-center space-x-6 text-sm">
            <Link to="/what-is-my-business-worth" className="flex items-center bg-[#E31837] text-white px-3 py-0.5 rounded-full text-xs font-semibold hover:bg-[#C0152E] transition-colors">
              Free Business Valuation →
            </Link>
            <a href={`tel:${CONTACT_INFO.PRIMARY_PHONE_RAW}`} className="flex items-center hover:text-[#F59E0B] transition-colors">
              <Phone className="w-4 h-4 mr-1" />
              {CONTACT_INFO.PRIMARY_PHONE}
            </a>
            <a href={`tel:${CONTACT_INFO.OFFICE_PHONE_RAW}`} className="flex items-center hover:text-[#F59E0B] transition-colors">
              <Phone className="w-4 h-4 mr-1" />
              {CONTACT_INFO.OFFICE_PHONE} (Office)
            </a>
            <a href={`mailto:${CONTACT_INFO.EMAIL}`} className="flex items-center hover:text-[#F59E0B] transition-colors">
              <Mail className="w-4 h-4 mr-1" />
              {CONTACT_INFO.EMAIL}
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className={`sticky top-0 z-50 bg-white transition-all duration-300 ${
        isScrolled ? 'header-shadow' : ''
      }`}>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3">
              <img 
                src="/team-shailesh-logo.png"
                alt="Team Shailesh"
                className="h-[70px] w-auto object-contain"
              />
              <img 
                src="/remax-balloon.png" 
                alt="RE/MAX" 
                className="h-10 w-auto object-contain mb-1"
                width="36"
                height="40"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8" ref={desktopNavRef}>
              {navLinks.map((link) => (
                <div
                  key={link.name}
                  className="relative group"
                  onMouseEnter={() => link.dropdown && setActiveDropdown(link.name)}
                  onMouseLeave={() => setActiveDropdown(null)}
                  onClick={() => link.dropdown && setActiveDropdown(activeDropdown === link.name ? null : link.name)}
                >
                  {link.isExternal ? (
                    <span
                      className={`flex items-center font-medium transition-colors py-2 cursor-pointer text-gray-700 hover:text-[#1e3a8a]`}
                    >
                      {link.name}
                      {link.dropdown && <ChevronDown className="w-4 h-4 ml-1" />}
                    </span>
                  ) : (
                    <Link
                      to={link.path}
                      className={`flex items-center font-medium transition-colors py-2 ${
                        isActive(link.path)
                          ? 'text-[#1e3a8a] font-semibold'
                          : 'text-gray-700 hover:text-[#1e3a8a]'
                      }`}
                    >
                      {link.name}
                      {link.dropdown && <ChevronDown className="w-4 h-4 ml-1" />}
                    </Link>
                  )}

                  {/* Dropdown Menu */}
                  {link.dropdown && activeDropdown === link.name && (
                    <div className="absolute top-full left-0 pt-0 w-56 bg-white rounded-lg shadow-xl py-2 animate-fadeIn border border-gray-100 z-50">
                      {link.dropdown.map((item) => (
                        item.isExternal ? (
                          <a
                            key={item.name}
                            href={item.path}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-[#1e3a8a] transition-colors"
                            onClick={() => setActiveDropdown(null)}
                          >
                            {item.name}
                          </a>
                        ) : (
                          <Link
                            key={item.name}
                            to={item.path}
                            className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-[#1e3a8a] transition-colors"
                            onClick={() => setActiveDropdown(null)}
                          >
                            {item.name}
                          </Link>
                        )
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* CTA + Buyer Portal */}
            <div className="hidden lg:flex items-center gap-3">
              {isAuthenticated && buyer ? (
                <Link to="/buyer/portal">
                  <Button variant="outline" className="border-[#2b5298] text-[#2b5298] hover:bg-[#2b5298] hover:text-white font-medium px-4">
                    <User className="w-4 h-4 mr-1.5" />
                    {buyer.name.split(' ')[0]}
                  </Button>
                </Link>
              ) : (
                <Link to="/buyer/portal">
                  <Button variant="outline" className="border-gray-300 text-gray-600 hover:border-[#2b5298] hover:text-[#2b5298] font-medium px-4">
                    <User className="w-4 h-4 mr-1.5" />
                    Sign In
                  </Button>
                </Link>
              )}
              <Button
                onClick={() => {
                  const calendarUrl = 'https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ2Xwu_DZJc1g46R6Shjf6vfSyFVPODSeUFNEH3oJ_NUlvQljzaNqmGDNgGM5aPPX-PGJpk0_gnm';
                  window.open(calendarUrl, '_blank');
                }}
                className="bg-[#E31837] hover:bg-[#D97706] text-white font-semibold px-6"
              >
                Book a Consultation
              </Button>
            </div>

            {/* Mobile: tap-to-call + hamburger */}
            <div className="lg:hidden flex items-center gap-1">
              <a
                href={`tel:${CONTACT_INFO.PRIMARY_PHONE_RAW}`}
                className="flex items-center justify-center min-h-[44px] min-w-[44px] text-[#1e3a8a]"
                aria-label={`Call ${CONTACT_INFO.PRIMARY_PHONE}`}
                style={{ touchAction: 'manipulation' }}
              >
                <Phone size={22} />
              </a>
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
                style={{ touchAction: 'manipulation' }}
              >
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div
              className="lg:hidden fixed inset-0 top-[80px] bg-white z-50 overflow-y-auto"
              style={{ maxHeight: 'calc(100dvh - 80px)', WebkitOverflowScrolling: 'touch' }}
            >
              <div className="px-4 py-2">
                {navLinks.map((link) => (
                  <div key={link.name} className="border-b border-gray-100">
                    {link.dropdown ? (
                      /* Items with sub-menus: accordion toggle */
                      <>
                        <button
                          className="flex items-center justify-between w-full py-3 font-medium text-gray-700"
                          style={{ touchAction: 'manipulation', minHeight: '44px' }}
                          onClick={() =>
                            setMobileExpandedDropdown(
                              mobileExpandedDropdown === link.name ? null : link.name
                            )
                          }
                          aria-expanded={mobileExpandedDropdown === link.name}
                        >
                          <span className={isActive(link.path) ? 'text-[#1e3a8a] font-semibold' : ''}>
                            {link.name}
                          </span>
                          <ChevronDown
                            className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
                              mobileExpandedDropdown === link.name ? 'rotate-180' : ''
                            }`}
                          />
                        </button>
                        {mobileExpandedDropdown === link.name && (
                          <div className="ml-4 pb-2 space-y-1">
                            {link.dropdown.map((item) =>
                              item.isExternal ? (
                                <a
                                  key={item.name}
                                  href={item.path}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center py-3 text-gray-600 text-sm"
                                  style={{ touchAction: 'manipulation', minHeight: '44px' }}
                                  onClick={() => setIsMobileMenuOpen(false)}
                                >
                                  {item.name}
                                </a>
                              ) : (
                                <Link
                                  key={item.name}
                                  to={item.path}
                                  className="flex items-center py-3 text-gray-600 text-sm"
                                  style={{ touchAction: 'manipulation', minHeight: '44px' }}
                                  onClick={() => setIsMobileMenuOpen(false)}
                                >
                                  {item.name}
                                </Link>
                              )
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      /* Plain links: navigate directly */
                      <Link
                        to={link.path}
                        className={`flex items-center py-3 font-medium ${
                          isActive(link.path) ? 'text-[#1e3a8a] font-semibold' : 'text-gray-700'
                        }`}
                        style={{ touchAction: 'manipulation', minHeight: '44px' }}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {link.name}
                      </Link>
                    )}
                  </div>
                ))}

                {/* Tap-to-call strip */}
                <a
                  href={`tel:${CONTACT_INFO.PRIMARY_PHONE_RAW}`}
                  className="flex items-center gap-3 w-full mt-4 bg-[#1e3a8a] text-white rounded-lg px-4 py-3 font-medium"
                  style={{ touchAction: 'manipulation', minHeight: '44px' }}
                >
                  <Phone className="w-5 h-5" />
                  {CONTACT_INFO.PRIMARY_PHONE}
                </a>

                {isAuthenticated && buyer ? (
                  <Link
                    to="/buyer/portal"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-2 w-full mt-3 border border-[#2b5298] text-[#2b5298] rounded-lg px-4 py-3 font-medium"
                    style={{ minHeight: '44px' }}
                  >
                    <User className="w-4 h-4" />
                    My Portal ({buyer.name.split(' ')[0]})
                  </Link>
                ) : (
                  <Link
                    to="/buyer/portal"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-2 w-full mt-3 border border-gray-300 text-gray-600 rounded-lg px-4 py-3 font-medium"
                    style={{ minHeight: '44px' }}
                  >
                    <User className="w-4 h-4" />
                    Buyer Sign In
                  </Link>
                )}
                <Button
                  onClick={() => {
                    const calendarUrl = 'https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ2Xwu_DZJc1g46R6Shjf6vfSyFVPODSeUFNEH3oJ_NUlvQljzaNqmGDNgGM5aPPX-PGJpk0_gnm';
                    window.open(calendarUrl, '_blank');
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full mt-3 bg-[#E31837] hover:bg-[#C0152E] text-white py-3"
                  style={{ minHeight: '44px', touchAction: 'manipulation' }}
                >
                  Book a Consultation
                </Button>
                <div className="h-20" />
              </div>
            </div>
          )}
        </div>
      </nav>
    </>
  );
};

export default NewNavbar;